import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { Navbar } from '../components';
import { Footer } from '../containers';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebaseconfig';
import SEOMeta from '../components/SEOMeta/SEOMeta';
import { getProblemById } from '../data/dsaProblemCatalog';
import './ProblemDetailPage.css';

const PROGRESS_STORAGE_KEY = 'algodoor_dsa_progress';
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const readLocalProgress = (userKey) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(PROGRESS_STORAGE_KEY) || '{}');
    return parsed[userKey] || { solved: [] };
  } catch (_) {
    return { solved: [] };
  }
};

const writeLocalProgress = (userKey, payload) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(PROGRESS_STORAGE_KEY) || '{}');
    parsed[userKey] = payload;
    localStorage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(parsed));
  } catch (_) {
    // fallback no-op
  }
};

const VISUAL_SAMPLES = {
  'array-pointer': { nums: [2, 7, 11, 15], target: 9 },
  'binary-search': { nums: [-1, 0, 3, 5, 9, 12], target: 9 },
  stack: { text: '([{}])' },
  window: { prices: [7, 1, 5, 3, 6, 4] },
  intervals: { intervals: [[1, 3], [2, 6], [8, 10], [15, 18]] },
  dp: { n: 7 },
  tree: {
    nodes: [
      { id: 'A', x: 50, y: 14, label: '10' },
      { id: 'B', x: 30, y: 44, label: '6' },
      { id: 'C', x: 70, y: 44, label: '15' },
      { id: 'D', x: 20, y: 76, label: '3' },
      { id: 'E', x: 40, y: 76, label: '8' },
      { id: 'F', x: 62, y: 76, label: '12' },
      { id: 'G', x: 80, y: 76, label: '18' },
    ],
    edges: [['A', 'B'], ['A', 'C'], ['B', 'D'], ['B', 'E'], ['C', 'F'], ['C', 'G']],
    visitOrder: ['A', 'B', 'D', 'E', 'C', 'F', 'G'],
  },
  graph: {
    nodes: [
      { id: '1', x: 18, y: 20 },
      { id: '2', x: 52, y: 12 },
      { id: '3', x: 82, y: 26 },
      { id: '4', x: 24, y: 72 },
      { id: '5', x: 58, y: 62 },
      { id: '6', x: 84, y: 78 },
    ],
    edges: [['1', '2'], ['1', '4'], ['2', '3'], ['2', '5'], ['4', '5'], ['5', '6'], ['3', '6']],
    visitOrder: ['1', '2', '4', '3', '5', '6'],
  },
  'linked-list': {
    values: [5, 9, 1, 4, 7],
  },
  queue: {
    values: [11, 15, 20, 25],
  },
};

const isIndexPair = (value, length) => Array.isArray(value)
  && value.length === 2
  && value.every((v) => Number.isInteger(v) && v >= 0 && v < length);

const sameJson = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const inferSampleFromProblem = (problem, visualType) => {
  const fallback = VISUAL_SAMPLES[visualType] || VISUAL_SAMPLES.dp;
  const firstTest = problem?.question?.tests?.[0];
  const args = firstTest?.input || [];

  if (!firstTest || !Array.isArray(args)) return fallback;

  if (visualType === 'array-pointer' || visualType === 'binary-search') {
    if (problem.question.functionName === 'findMedianSortedArrays' && Array.isArray(args[0]) && Array.isArray(args[1])) {
      const merged = [...args[0], ...args[1]].sort((a, b) => a - b);
      return {
        mode: 'median',
        nums1: args[0],
        nums2: args[1],
        nums: merged,
        expected: firstTest.expected,
        rawInput: firstTest.input,
      };
    }
    if (problem.question.functionName === 'findMin' && Array.isArray(args[0])) {
      return {
        mode: 'find-min-rotated',
        nums: args[0],
        expected: firstTest.expected,
        rawInput: firstTest.input,
      };
    }
    if (problem.question.functionName === 'threeSum' && Array.isArray(args[0])) {
      return {
        mode: 'three-sum',
        nums: args[0],
        expected: firstTest.expected,
        rawInput: firstTest.input,
      };
    }
    if (problem.question.functionName === 'findKthLargest' && Array.isArray(args[0]) && typeof args[1] === 'number') {
      return {
        mode: 'kth-largest',
        nums: args[0],
        k: args[1],
        expected: firstTest.expected,
        rawInput: firstTest.input,
      };
    }
    if (problem.question.functionName === 'moveZeroesStable' && Array.isArray(args[0])) {
      return {
        mode: 'move-zeroes',
        nums: args[0],
        expected: firstTest.expected,
        rawInput: firstTest.input,
      };
    }
    if (problem.question.functionName === 'searchInsert' && Array.isArray(args[0]) && typeof args[1] === 'number') {
      return {
        mode: 'search-insert',
        nums: args[0],
        target: args[1],
        expected: firstTest.expected,
        rawInput: firstTest.input,
      };
    }
    if (Array.isArray(args[0]) && typeof args[1] === 'number') {
      return {
        mode: 'pair-sum',
        nums: args[0],
        target: args[1],
        expected: firstTest.expected,
        rawInput: firstTest.input,
      };
    }
    return fallback;
  }

  if (visualType === 'stack') {
    if (problem.question.functionName === 'dailyTemperatures' && Array.isArray(args[0])) {
      return {
        mode: 'daily-temperatures',
        temps: args[0],
        expected: firstTest.expected,
        rawInput: firstTest.input,
      };
    }
    const textArg = args.find((arg) => typeof arg === 'string');
    return textArg ? { text: textArg, expected: firstTest.expected, rawInput: firstTest.input } : fallback;
  }

  if (visualType === 'window') {
    if (typeof args[0] === 'string') {
      return {
        mode: 'substring',
        text: args[0],
        expected: firstTest.expected,
        rawInput: firstTest.input,
      };
    }
    const pricesArg = args.find((arg) => Array.isArray(arg) && arg.every((v) => typeof v === 'number'));
    return pricesArg
      ? {
        mode: problem.question.functionName === 'maxArea' ? 'container' : 'profit',
        prices: pricesArg,
        expected: firstTest.expected,
        rawInput: firstTest.input,
      }
      : fallback;
  }

  if (visualType === 'intervals') {
    const intervalsArg = args.find((arg) => Array.isArray(arg)
      && arg.every((row) => Array.isArray(row) && row.every((v) => typeof v === 'number')));
    return intervalsArg ? { intervals: intervalsArg, expected: firstTest.expected, rawInput: firstTest.input } : fallback;
  }

  if (visualType === 'dp') {
    if (problem.question.functionName === 'maxSubArrayKadane' && Array.isArray(args[0])) {
      return {
        mode: 'max-subarray',
        nums: args[0],
        expected: firstTest.expected,
        rawInput: firstTest.input,
      };
    }
    if (problem.question.functionName === 'coinChange' && Array.isArray(args[0]) && typeof args[1] === 'number') {
      return {
        mode: 'coin-change',
        coins: args[0],
        amount: Math.max(args[1], 0),
        expected: firstTest.expected,
        rawInput: firstTest.input,
      };
    }
    const nArg = args.find((arg) => typeof arg === 'number');
    return typeof nArg === 'number'
      ? {
        mode: 'stairs',
        n: Math.max(nArg, 2),
        expected: firstTest.expected,
        rawInput: firstTest.input,
      }
      : fallback;
  }

  if (visualType === 'linked-list' || visualType === 'queue') {
    if (problem.question.functionName === 'addTwoNumbers'
      && Array.isArray(args[0])
      && Array.isArray(args[1])
      && args[0].every((v) => typeof v === 'number')
      && args[1].every((v) => typeof v === 'number')) {
      return {
        mode: 'add-two-numbers',
        l1: args[0],
        l2: args[1],
        expected: firstTest.expected,
        rawInput: firstTest.input,
      };
    }

    if (problem.question.functionName === 'simulateLFU'
      && typeof args[0] === 'number'
      && Array.isArray(args[1])) {
      return {
        mode: 'lfu',
        capacity: Math.max(0, args[0]),
        operations: args[1],
        expected: firstTest.expected,
        rawInput: firstTest.input,
      };
    }

    const valuesArg = args.find((arg) => Array.isArray(arg) && arg.every((v) => typeof v === 'number'));
    if (!valuesArg) return fallback;

    let stopAtIndex = null;
    if (typeof args[1] === 'number' && isIndexPair(firstTest.expected, valuesArg.length)) {
      const candidate = Math.max(firstTest.expected[0], firstTest.expected[1]);
      if (candidate >= 0 && candidate < valuesArg.length) stopAtIndex = candidate;
    }

    return { values: valuesArg, stopAtIndex, expected: firstTest.expected, rawInput: firstTest.input };
  }

  if (visualType === 'tree') {
    if (problem.question.functionName === 'countPrefixMatches'
      && Array.isArray(args[0])
      && Array.isArray(args[1])) {
      return {
        mode: 'trie-prefix',
        words: args[0],
        queries: args[1],
        expected: firstTest.expected,
        rawInput: firstTest.input,
      };
    }
    return fallback;
  }

  if (visualType === 'graph') {
    if (problem.question.functionName === 'numIslands' && Array.isArray(args[0])) {
      return {
        mode: 'num-islands',
        grid: args[0],
        expected: firstTest.expected,
        rawInput: firstTest.input,
      };
    }
    if (problem.question.functionName === 'canFinish' && typeof args[0] === 'number' && Array.isArray(args[1])) {
      return {
        mode: 'course-schedule',
        numCourses: args[0],
        prerequisites: args[1],
        expected: firstTest.expected,
        rawInput: firstTest.input,
      };
    }
    if (problem.question.functionName === 'ladderLength'
      && typeof args[0] === 'string'
      && typeof args[1] === 'string'
      && Array.isArray(args[2])) {
      return {
        mode: 'word-ladder',
        beginWord: args[0],
        endWord: args[1],
        wordList: args[2],
        expected: firstTest.expected,
        rawInput: firstTest.input,
      };
    }
    if (problem.question.functionName === 'shortestPathBinaryMatrix' && Array.isArray(args[0])) {
      return {
        mode: 'shortest-path-matrix',
        grid: args[0],
        expected: firstTest.expected,
        rawInput: firstTest.input,
      };
    }
    return fallback;
  }

  return fallback;
};

const normalizeSample = (visualType, sample) => {
  if (visualType === 'array-pointer' || visualType === 'binary-search') {
    if (!Array.isArray(sample.nums) || sample.nums.length === 0) {
      return VISUAL_SAMPLES[visualType];
    }
    if (sample.mode !== 'median' && sample.mode !== 'three-sum' && sample.mode !== 'kth-largest' && sample.mode !== 'find-min-rotated' && sample.mode !== 'move-zeroes' && typeof sample.target !== 'number') {
      return VISUAL_SAMPLES[visualType];
    }
  }

  if (visualType === 'stack') {
    if (sample.mode === 'daily-temperatures') {
      if (!Array.isArray(sample.temps) || sample.temps.length === 0) return VISUAL_SAMPLES.stack;
    } else if (typeof sample.text !== 'string' || sample.text.length === 0) {
      return VISUAL_SAMPLES.stack;
    }
  }

  if (visualType === 'window') {
    if (sample.mode === 'substring') {
      if (typeof sample.text !== 'string' || sample.text.length === 0) return VISUAL_SAMPLES.window;
    } else if (!Array.isArray(sample.prices) || sample.prices.length === 0) {
      return VISUAL_SAMPLES.window;
    }
  }

  if (visualType === 'intervals') {
    if (!Array.isArray(sample.intervals) || sample.intervals.length === 0) return VISUAL_SAMPLES.intervals;
  }

  if (visualType === 'dp') {
    if (sample.mode === 'coin-change') {
      if (!Array.isArray(sample.coins) || typeof sample.amount !== 'number') return VISUAL_SAMPLES.dp;
    } else if (sample.mode === 'max-subarray') {
      if (!Array.isArray(sample.nums) || sample.nums.length === 0) return VISUAL_SAMPLES.dp;
    } else if (typeof sample.n !== 'number' || sample.n < 2) {
      return VISUAL_SAMPLES.dp;
    }
  }

  if (visualType === 'linked-list' || visualType === 'queue') {
    if (sample.mode === 'add-two-numbers') {
      if (!Array.isArray(sample.l1) || !Array.isArray(sample.l2)) return VISUAL_SAMPLES[visualType];
    } else if (sample.mode === 'lfu') {
      if (typeof sample.capacity !== 'number' || !Array.isArray(sample.operations)) return VISUAL_SAMPLES[visualType];
    } else if (!Array.isArray(sample.values) || sample.values.length === 0) {
      return VISUAL_SAMPLES[visualType];
    }
  }

  if (visualType === 'tree') {
    if (sample.mode === 'trie-prefix') {
      if (!Array.isArray(sample.words) || !Array.isArray(sample.queries)) return VISUAL_SAMPLES.tree;
    }
  }

  if (visualType === 'graph') {
    if ((sample.mode === 'num-islands' || sample.mode === 'shortest-path-matrix') && !Array.isArray(sample.grid)) {
      return VISUAL_SAMPLES.graph;
    }
    if (sample.mode === 'course-schedule'
      && (typeof sample.numCourses !== 'number' || !Array.isArray(sample.prerequisites))) {
      return VISUAL_SAMPLES.graph;
    }
    if (sample.mode === 'word-ladder'
      && (typeof sample.beginWord !== 'string' || typeof sample.endWord !== 'string' || !Array.isArray(sample.wordList))) {
      return VISUAL_SAMPLES.graph;
    }
  }

  return sample;
};

const buildVisualizationSteps = (problem) => {
  const visualType = problem.visualType;
  const sample = normalizeSample(visualType, inferSampleFromProblem(problem, visualType));
  if (visualType === 'array-pointer') {
    const { nums, target, expected } = sample;

    if (sample.mode === 'kth-largest') {
      const sorted = [...nums].sort((a, b) => b - a);
      const targetIndex = Math.max(0, Math.min((sample.k || 1) - 1, sorted.length - 1));
      return sorted.map((_, idx) => ({
        nums: sorted,
        current: idx,
        match: idx === targetIndex ? idx : -1,
        caption: idx === targetIndex
          ? `Pointer reached index ${idx} in descending order. This is the kth largest value ${sorted[idx]}, so traversal stops.`
          : `Scanning descending order: index ${idx} has value ${sorted[idx]}. Continue until reaching k - 1.`,
      })).slice(0, targetIndex + 1);
    }

    if (sample.mode === 'three-sum') {
      const sorted = [...nums].sort((a, b) => a - b);
      const steps = [];
      let done = false;
      for (let i = 0; i < sorted.length - 2 && !done; i += 1) {
        let left = i + 1;
        let right = sorted.length - 1;
        while (left < right) {
          const total = sorted[i] + sorted[left] + sorted[right];
          const found = total === 0;
          steps.push({
            nums: sorted,
            current: left,
            match: right,
            anchor: i,
            caption: found
              ? `Triplet found at indices (${i}, ${left}, ${right}) with values (${sorted[i]}, ${sorted[left]}, ${sorted[right]}). Sum is 0, so this walkthrough stops.`
              : `Evaluate indices (${i}, ${left}, ${right}) with sum ${total}. Move pointers to approach 0.`,
          });
          if (found) {
            done = true;
            break;
          }
          if (total < 0) left += 1;
          else right -= 1;
        }
      }
      return steps.length ? steps : [{ nums: sorted, current: 0, match: -1, caption: 'No zero-sum triplet found for this testcase.' }];
    }

    if (sample.mode === 'move-zeroes') {
      const arr = [...nums];
      let write = 0;
      const steps = [];
      for (let read = 0; read < arr.length; read += 1) {
        if (arr[read] !== 0) {
          const temp = arr[write];
          arr[write] = arr[read];
          arr[read] = temp;
          write += 1;
        }
        const matchedExpected = Array.isArray(sample.expected) ? sameJson(arr, sample.expected) : false;
        steps.push({
          mode: 'move-zeroes',
          nums: [...arr],
          current: read,
          write,
          match: Math.max(0, write - 1),
          caption: `Read pointer is at index ${read}. Write pointer is ${write}. Non-zero values are compacted to the left while zeros shift to the end.`,
          matchedExpected,
        });
      }
      return steps;
    }

    const expectedPair = isIndexPair(expected, nums.length) ? expected : null;
    const stopAtIndex = expectedPair ? Math.max(expectedPair[0], expectedPair[1]) : null;
    const anchorIndex = expectedPair ? Math.min(expectedPair[0], expectedPair[1]) : null;
    const seen = new Map();
    const steps = [];
    for (let i = 0; i < nums.length; i += 1) {
      if (stopAtIndex !== null && i > stopAtIndex) break;
      const need = target - nums[i];
      const computedMatch = seen.has(need) ? seen.get(need) : -1;
      const expectedMatch = stopAtIndex !== null && i === stopAtIndex ? anchorIndex : -1;
      const match = expectedMatch !== -1 ? expectedMatch : computedMatch;
      const caption = match !== -1
        ? `Step ${i + 1}: iterator i points to value ${nums[i]}. Complement needed is ${need}. The hash map already contains ${need} at index ${match}, so we found the final pair [${match}, ${i}] and stop traversal.`
        : `Step ${i + 1}: iterator i points to value ${nums[i]}. Complement needed is ${need}. It is not present in the hash map yet, so we store (${nums[i]} -> ${i}) and move the iterator to the next box.`;
      steps.push({ nums, target, current: i, match, caption, stopAtIndex });
      if (match !== -1) break;
      seen.set(nums[i], i);
    }
    return steps;
  }

  if (visualType === 'binary-search') {
    const { nums, target, expected } = sample;
    if (sample.mode === 'median') {
      const n = nums.length;
      const leftMedian = Math.floor((n - 1) / 2);
      const rightMedian = Math.floor(n / 2);
      const isEven = n % 2 === 0;
      const caption = isEven
        ? `Merged sorted sequence is ${JSON.stringify(nums)}. Middle indices are ${leftMedian} and ${rightMedian}, so median is average of ${nums[leftMedian]} and ${nums[rightMedian]}.`
        : `Merged sorted sequence is ${JSON.stringify(nums)}. Middle index is ${leftMedian}, so median is ${nums[leftMedian]}.`;
      return [{
        mode: 'median',
        nums,
        left: 0,
        right: n - 1,
        mid: leftMedian,
        match: isEven ? rightMedian : -1,
        found: true,
        caption,
      }];
    }
    if (sample.mode === 'find-min-rotated') {
      let left = 0;
      let right = nums.length - 1;
      const steps = [];
      while (left < right) {
        const mid = Math.floor((left + right) / 2);
        const moveRight = nums[mid] <= nums[right];
        steps.push({
          mode: 'find-min-rotated',
          nums,
          left,
          right,
          mid,
          found: false,
          caption: moveRight
            ? `nums[mid] = ${nums[mid]} is <= nums[right] = ${nums[right]}, so minimum is in left half including mid. Move right to ${mid}.`
            : `nums[mid] = ${nums[mid]} is > nums[right] = ${nums[right]}, so minimum is in right half. Move left to ${mid + 1}.`,
        });
        if (moveRight) right = mid;
        else left = mid + 1;
      }
      steps.push({
        mode: 'find-min-rotated',
        nums,
        left,
        right,
        mid: left,
        found: true,
        caption: `Pointers converged at index ${left}. Minimum value is ${nums[left]}.`,
      });
      return steps;
    }
    if (sample.mode === 'search-insert') {
      let left = 0;
      let right = nums.length - 1;
      const steps = [];
      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        if (nums[mid] === target) {
          steps.push({
            mode: 'search-insert',
            nums,
            target,
            left,
            right,
            mid,
            found: true,
            caption: `Target ${target} found at index ${mid}.`,
          });
          return steps;
        }
        steps.push({
          mode: 'search-insert',
          nums,
          target,
          left,
          right,
          mid,
          found: false,
          caption: `nums[mid] is ${nums[mid]}. Continue in the half where ${target} can appear or be inserted.`,
        });
        if (nums[mid] < target) left = mid + 1;
        else right = mid - 1;
      }
      steps.push({
        mode: 'search-insert',
        nums,
        target,
        left,
        right,
        mid: Math.max(0, Math.min(left, nums.length - 1)),
        found: true,
        caption: `Target ${target} is not present. Insertion index is ${left}.`,
      });
      return steps;
    }
    let left = 0;
    let right = nums.length - 1;
    const steps = [];
    const expectedIndex = Number.isInteger(expected) && expected >= -1 && expected < nums.length ? expected : null;
    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const found = expectedIndex !== null ? mid === expectedIndex : nums[mid] === target;
      steps.push({
        nums,
        target,
        left,
        right,
        mid,
        found,
        caption: found
          ? `Search window is [${left}, ${right}] and mid is ${mid}. Since nums[mid] = ${nums[mid]} equals target ${target}, the answer is index ${mid}.`
          : `Search window is [${left}, ${right}] and mid is ${mid} with value ${nums[mid]}. Compare nums[mid] with target ${target} and discard the half that cannot contain the answer.`,
      });
      if (found) break;
      if (nums[mid] < target) left = mid + 1;
      else right = mid - 1;
    }
    return steps;
  }

  if (visualType === 'stack') {
    if (sample.mode === 'daily-temperatures') {
      const temps = sample.temps;
      const stack = [];
      const out = Array(temps.length).fill(0);
      const steps = [];

      for (let i = 0; i < temps.length; i += 1) {
        while (stack.length && temps[i] > temps[stack[stack.length - 1]]) {
          const prev = stack.pop();
          out[prev] = i - prev;
        }
        stack.push(i);
        const matchedExpected = Array.isArray(sample.expected) ? sameJson(out, sample.expected) : false;
        steps.push({
          mode: 'daily-temperatures',
          temps,
          current: i,
          stack: [...stack],
          output: [...out],
          matchedExpected,
          caption: `Day ${i} temperature is ${temps[i]}. Maintain a decreasing stack of unresolved day indices and update waiting days when a warmer temperature appears.`,
        });
        if (matchedExpected) break;
      }

      return steps;
    }

    const { text, expected } = sample;
    const pairs = new Map([[')', '('], [']', '['], ['}', '{']]);
    const stack = [];
    const steps = [];
    const expectInvalid = expected === false;
    const chars = text.split('');
    for (let idx = 0; idx < chars.length; idx += 1) {
      const ch = chars[idx];
      let caption;
      if (pairs.has(ch)) {
        const top = stack.pop();
        const mismatch = top !== pairs.get(ch);
        caption = mismatch
          ? `Character '${ch}' closes '${pairs.get(ch)}', but stack top was '${top || 'empty'}'. Expression becomes invalid, so traversal stops.`
          : `Character '${ch}' is a closing bracket and matches stack top '${top}', so we continue.`;
        steps.push({ text, current: idx, stack: [...stack], caption, mismatch });
        if (expectInvalid && mismatch) break;
        continue;
      }
      stack.push(ch);
      caption = `Character '${ch}' is an opening bracket, so we push it onto the stack and wait for a matching closing bracket later.`;
      steps.push({ text, current: idx, stack: [...stack], caption, mismatch: false });
    }
    return steps;
  }

  if (visualType === 'window') {
    const { expected } = sample;
    if (sample.mode === 'substring') {
      const text = sample.text;
      const chars = text.split('');
      const lastSeen = new Map();
      let left = 0;
      let best = 0;
      const steps = [];

      for (let right = 0; right < chars.length; right += 1) {
        const ch = chars[right];
        if (lastSeen.has(ch) && lastSeen.get(ch) >= left) {
          left = lastSeen.get(ch) + 1;
        }
        lastSeen.set(ch, right);
        const windowLen = right - left + 1;
        best = Math.max(best, windowLen);
        const matchedExpected = typeof expected === 'number' ? best === expected : false;
        const remainingMax = chars.length - left;
        const canStop = matchedExpected && remainingMax <= best;

        steps.push({
          mode: 'substring',
          chars,
          left,
          current: right,
          best,
          windowLen,
          matchedExpected,
          caption: `Right pointer reads '${ch}' at index ${right}. Current window is s[${left}..${right}] with length ${windowLen}; best length so far is ${best}.`,
        });

        if (canStop) break;
      }

      return steps;
    }

    if (sample.mode === 'container') {
      const heights = sample.prices;
      let left = 0;
      let right = heights.length - 1;
      let best = 0;
      const steps = [];
      while (left < right) {
        const width = right - left;
        const area = width * Math.min(heights[left], heights[right]);
        best = Math.max(best, area);
        const matchedExpected = typeof expected === 'number' ? best === expected : false;
        steps.push({
          mode: 'container',
          prices: heights,
          minIdx: left,
          current: right,
          best,
          area,
          matchedExpected,
          caption: `Left line ${left} and right line ${right} form area ${area}. Best area so far is ${best}.`,
        });
        if (matchedExpected) break;
        if (heights[left] < heights[right]) left += 1;
        else right -= 1;
      }
      return steps;
    }

    const prices = sample.prices;
    let minPrice = Infinity;
    let minIdx = -1;
    let best = 0;
    const steps = [];
    for (let idx = 0; idx < prices.length; idx += 1) {
      const price = prices[idx];
      if (price < minPrice) {
        minPrice = price;
        minIdx = idx;
      }
      const candidate = price - minPrice;
      best = Math.max(best, candidate);
      const matchedExpected = typeof expected === 'number' ? best === expected : false;
      steps.push({
        mode: 'profit',
        prices,
        current: idx,
        minPrice,
        minIdx,
        best,
        matchedExpected,
        caption: `Sell-pointer is at day ${idx} with price ${price}. Buy-pointer is at day ${minIdx} with lowest seen price ${minPrice}. Current candidate profit is ${candidate}, and best profit so far is ${best}.`,
      });
      if (matchedExpected) break;
    }
    return steps;
  }

  if (visualType === 'intervals') {
    const sorted = [...sample.intervals].sort((a, b) => a[0] - b[0]);
    const expected = sample.expected;
    const merged = [[...sorted[0]]];
    const steps = [{
      sorted,
      current: 0,
      merged: merged.map((it) => [...it]),
      caption: `Intervals are first sorted by start time. Initialize merged list with [${sorted[0][0]}, ${sorted[0][1]}].`,
    }];
    for (let i = 1; i < sorted.length; i += 1) {
      const current = sorted[i];
      const last = merged[merged.length - 1];
      let caption = `Current interval [${current[0]}, ${current[1]}] starts after merged tail ends, so no overlap and we append it as a new block.`;
      if (current[0] <= last[1]) {
        last[1] = Math.max(last[1], current[1]);
        caption = `Current interval [${current[0]}, ${current[1]}] overlaps merged tail, so we extend merged end boundary to ${last[1]}.`;
      } else {
        merged.push([...current]);
      }
      const mergedNow = merged.map((it) => [...it]);
      steps.push({ sorted, current: i, merged: mergedNow, caption });
      if (Array.isArray(expected) && sameJson(mergedNow, expected)) break;
    }
    return steps;
  }

  if (visualType === 'tree') {
    if (sample.mode === 'trie-prefix') {
      const words = sample.words;
      const queries = sample.queries;
      const expected = Array.isArray(sample.expected) ? sample.expected : [];
      const prefixCount = new Map();
      words.forEach((word) => {
        for (let len = 1; len <= word.length; len += 1) {
          const prefix = word.slice(0, len);
          prefixCount.set(prefix, (prefixCount.get(prefix) || 0) + 1);
        }
      });

      const nodes = words.slice(0, 8).map((word, idx) => ({
        id: `w-${idx}`,
        x: 15 + ((idx % 4) * 22),
        y: 22 + (Math.floor(idx / 4) * 42),
        label: word,
      }));

      const root = { id: 'root', x: 50, y: 8, label: 'root' };
      const allNodes = [root, ...nodes];
      const edges = nodes.map((node) => ['root', node.id]);

      const steps = queries.map((query, idx) => {
        const count = prefixCount.get(query) || 0;
        const expectedValue = typeof expected[idx] === 'number' ? expected[idx] : count;
        return {
          mode: 'trie-prefix',
          nodes: allNodes,
          edges,
          activeId: nodes[idx % Math.max(nodes.length, 1)]?.id || 'root',
          query,
          count,
          expectedValue,
          caption: `Query prefix "${query}" matches ${count} inserted words. Expected output at this query index is ${expectedValue}.`,
        };
      });

      return steps.length ? steps : [{ mode: 'trie-prefix', nodes: allNodes, edges, activeId: 'root', caption: 'No prefix queries provided.' }];
    }

    const { nodes, edges, visitOrder } = VISUAL_SAMPLES.tree;
    return visitOrder.map((activeId, idx) => ({
      nodes,
      edges,
      activeId,
      caption: `Tree traversal step ${idx + 1}: visit node ${activeId}. Parent-child pointers remain fixed while traversal pointer moves to next branch.`,
    }));
  }

  if (visualType === 'graph') {
    if (sample.mode === 'course-schedule') {
      const n = Math.max(0, sample.numCourses);
      const prereq = sample.prerequisites;
      const indegree = Array(n).fill(0);
      const graph = Array.from({ length: n }, () => []);
      prereq.forEach(([course, pre]) => {
        if (course >= 0 && course < n && pre >= 0 && pre < n) {
          graph[pre].push(course);
          indegree[course] += 1;
        }
      });

      const nodes = Array.from({ length: n }, (_, i) => {
        const angle = (2 * Math.PI * i) / Math.max(n, 1);
        return {
          id: String(i),
          x: 50 + 34 * Math.cos(angle),
          y: 50 + 34 * Math.sin(angle),
          label: String(i),
        };
      });
      const edges = prereq.map(([course, pre]) => [String(pre), String(course)]);

      const queue = [];
      for (let i = 0; i < n; i += 1) if (indegree[i] === 0) queue.push(i);
      let taken = 0;
      const steps = [];

      for (let head = 0; head < queue.length; head += 1) {
        const node = queue[head];
        taken += 1;
        graph[node].forEach((next) => {
          indegree[next] -= 1;
          if (indegree[next] === 0) queue.push(next);
        });

        const done = taken === n;
        steps.push({
          mode: 'course-schedule',
          nodes,
          edges,
          activeId: String(node),
          taken,
          total: n,
          queueSnapshot: queue.slice(head + 1),
          caption: done
            ? `Processed course ${node}. All ${n} courses are now schedulable, so target output is true.`
            : `Processed course ${node}. Completed ${taken}/${n}. Queue now has [${queue.slice(head + 1).join(', ')}].`,
        });
        if (done) break;
      }

      if (steps.length === 0) {
        steps.push({
          mode: 'course-schedule',
          nodes,
          edges,
          activeId: n > 0 ? '0' : null,
          taken,
          total: n,
          queueSnapshot: [],
          caption: `No zero-indegree start node exists. Target output is ${String(taken === n)}.`,
        });
      }

      const target = taken === n;
      const expected = typeof sample.expected === 'boolean' ? sample.expected : target;
      const last = steps[steps.length - 1];
      last.caption = `${last.caption} Final decision: ${String(target)} (expected ${String(expected)}).`;
      return steps;
    }

    if (sample.mode === 'word-ladder') {
      const begin = sample.beginWord;
      const end = sample.endWord;
      const dict = new Set(sample.wordList);
      const words = Array.from(new Set([begin, ...sample.wordList])).slice(0, 20);
      const isNeighbor = (a, b) => {
        if (a.length !== b.length) return false;
        let diff = 0;
        for (let i = 0; i < a.length; i += 1) {
          if (a[i] !== b[i]) diff += 1;
          if (diff > 1) return false;
        }
        return diff === 1;
      };

      const nodes = words.map((word, i) => {
        const angle = (2 * Math.PI * i) / Math.max(words.length, 1);
        return {
          id: word,
          x: 50 + 36 * Math.cos(angle),
          y: 50 + 36 * Math.sin(angle),
          label: word,
        };
      });
      const edges = [];
      for (let i = 0; i < words.length; i += 1) {
        for (let j = i + 1; j < words.length; j += 1) {
          if (isNeighbor(words[i], words[j])) edges.push([words[i], words[j]]);
        }
      }

      const neighbors = (word) => {
        const out = [];
        const chars = word.split('');
        for (let i = 0; i < chars.length; i += 1) {
          const original = chars[i];
          for (let code = 97; code <= 122; code += 1) {
            const ch = String.fromCharCode(code);
            if (ch === original) continue;
            chars[i] = ch;
            const next = chars.join('');
            if (dict.has(next)) out.push(next);
          }
          chars[i] = original;
        }
        return out;
      };

      const queue = [[begin, 1]];
      const seen = new Set([begin]);
      const steps = [];
      let foundDist = 0;

      for (let head = 0; head < queue.length; head += 1) {
        const [word, dist] = queue[head];
        const found = word === end;
        steps.push({
          mode: 'word-ladder',
          nodes,
          edges,
          activeId: word,
          distance: dist,
          found,
          caption: found
            ? `Reached end word "${end}" at distance ${dist}. This is the target output.`
            : `Visit word "${word}" at level ${dist}. Generate one-letter transformations and enqueue unseen valid words.`,
        });
        if (found) {
          foundDist = dist;
          break;
        }

        neighbors(word).forEach((next) => {
          if (!seen.has(next)) {
            seen.add(next);
            queue.push([next, dist + 1]);
          }
        });
      }

      const expected = typeof sample.expected === 'number' ? sample.expected : foundDist;
      if (steps.length === 0) {
        steps.push({ mode: 'word-ladder', nodes, edges, activeId: begin, distance: 1, found: false, caption: 'No traversal steps available.' });
      }
      steps[steps.length - 1].caption = `${steps[steps.length - 1].caption} Final ladder length is ${foundDist} (expected ${expected}).`;
      return steps;
    }

    if (sample.mode === 'num-islands') {
      const grid = sample.grid;
      const rows = grid.length;
      const cols = rows ? grid[0].length : 0;
      const cellId = (r, c) => `${r},${c}`;
      const nodes = [];
      const edges = [];
      for (let r = 0; r < rows; r += 1) {
        for (let c = 0; c < cols; c += 1) {
          nodes.push({
            id: cellId(r, c),
            x: 12 + (c * (74 / Math.max(cols - 1, 1))),
            y: 12 + (r * (74 / Math.max(rows - 1, 1))),
            label: String(grid[r][c]),
          });
          if (grid[r][c] === 1) {
            if (r + 1 < rows && grid[r + 1][c] === 1) edges.push([cellId(r, c), cellId(r + 1, c)]);
            if (c + 1 < cols && grid[r][c + 1] === 1) edges.push([cellId(r, c), cellId(r, c + 1)]);
          }
        }
      }

      const seen = Array.from({ length: rows }, () => Array(cols).fill(false));
      const dirs = [[1, 0], [-1, 0], [0, 1], [0, -1]];
      const steps = [];
      let islands = 0;

      const stack = [];
      for (let r = 0; r < rows; r += 1) {
        for (let c = 0; c < cols; c += 1) {
          if (grid[r][c] !== 1 || seen[r][c]) continue;
          islands += 1;
          stack.push([r, c]);
          seen[r][c] = true;
          while (stack.length) {
            const [cr, cc] = stack.pop();
            steps.push({
              mode: 'num-islands',
              nodes,
              edges,
              activeId: cellId(cr, cc),
              islands,
              caption: `Visiting land cell (${cr}, ${cc}). Islands discovered so far: ${islands}.`,
            });
            dirs.forEach(([dr, dc]) => {
              const nr = cr + dr;
              const nc = cc + dc;
              if (nr >= 0 && nc >= 0 && nr < rows && nc < cols && grid[nr][nc] === 1 && !seen[nr][nc]) {
                seen[nr][nc] = true;
                stack.push([nr, nc]);
              }
            });
          }
        }
      }

      const expected = typeof sample.expected === 'number' ? sample.expected : islands;
      if (steps.length === 0) {
        steps.push({ mode: 'num-islands', nodes, edges, activeId: null, islands: 0, caption: 'No land cells found. Target output is 0 islands.' });
      }
      steps[steps.length - 1].caption = `${steps[steps.length - 1].caption} Final island count is ${islands} (expected ${expected}).`;
      return steps;
    }

    if (sample.mode === 'shortest-path-matrix') {
      const grid = sample.grid;
      const n = grid.length;
      const cellId = (r, c) => `${r},${c}`;
      const nodes = [];
      const edges = [];
      for (let r = 0; r < n; r += 1) {
        for (let c = 0; c < n; c += 1) {
          nodes.push({
            id: cellId(r, c),
            x: 12 + (c * (74 / Math.max(n - 1, 1))),
            y: 12 + (r * (74 / Math.max(n - 1, 1))),
            label: String(grid[r][c]),
          });
        }
      }

      const dirs = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]];
      const seen = Array.from({ length: n }, () => Array(n).fill(false));
      const queue = [];
      const steps = [];

      if (n && grid[0][0] === 0) {
        queue.push([0, 0, 1]);
        seen[0][0] = true;
      }

      let answer = -1;
      for (let head = 0; head < queue.length; head += 1) {
        const [r, c, d] = queue[head];
        steps.push({
          mode: 'shortest-path-matrix',
          nodes,
          edges,
          activeId: cellId(r, c),
          distance: d,
          caption: `BFS visits cell (${r}, ${c}) at path length ${d}.`,
        });
        if (r === n - 1 && c === n - 1) {
          answer = d;
          break;
        }
        dirs.forEach(([dr, dc]) => {
          const nr = r + dr;
          const nc = c + dc;
          if (nr >= 0 && nc >= 0 && nr < n && nc < n && grid[nr][nc] === 0 && !seen[nr][nc]) {
            seen[nr][nc] = true;
            queue.push([nr, nc, d + 1]);
            edges.push([cellId(r, c), cellId(nr, nc)]);
          }
        });
      }

      const expected = typeof sample.expected === 'number' ? sample.expected : answer;
      if (steps.length === 0) {
        steps.push({ mode: 'shortest-path-matrix', nodes, edges, activeId: null, distance: -1, caption: 'Start is blocked, so no path exists.' });
      }
      steps[steps.length - 1].caption = `${steps[steps.length - 1].caption} Final shortest path is ${answer} (expected ${expected}).`;
      return steps;
    }

    const { nodes, edges, visitOrder } = VISUAL_SAMPLES.graph;
    return visitOrder.map((activeId, idx) => ({
      nodes,
      edges,
      activeId,
      caption: `Graph traversal step ${idx + 1}: active node is ${activeId}. We explore neighboring rounded nodes through connecting edges.`,
    }));
  }

  if (visualType === 'linked-list') {
    if (sample.mode === 'add-two-numbers') {
      const l1 = sample.l1;
      const l2 = sample.l2;
      const size = Math.max(l1.length, l2.length);
      const out = [];
      let carry = 0;
      const steps = [];

      for (let i = 0; i < size || carry; i += 1) {
        const a = i < l1.length ? l1[i] : 0;
        const b = i < l2.length ? l2[i] : 0;
        const sum = a + b + carry;
        const digit = sum % 10;
        carry = Math.floor(sum / 10);
        out.push(digit);
        steps.push({
          mode: 'add-two-numbers',
          current: i,
          l1,
          l2,
          a,
          b,
          sum,
          digit,
          carry,
          output: [...out],
          caption: `Position ${i}: ${a} + ${b} + carry gives ${sum}. Write digit ${digit} and carry ${carry} to next node.`,
        });
      }

      return steps;
    }

    const { values } = sample;
    const maxIndex = typeof sample.stopAtIndex === 'number'
      ? sample.stopAtIndex
      : values.length - 1;
    return values.slice(0, maxIndex + 1).map((_, idx) => ({
      values,
      current: idx,
      caption: idx === maxIndex
        ? `Linked list pointer reached node ${idx} (value ${values[idx]}). Required output is satisfied here, so traversal stops.`
        : `Linked list pointer is at node ${idx} (value ${values[idx]}). Follow next links node-by-node for linear traversal.`,
    }));
  }

  if (visualType === 'queue') {
    if (sample.mode === 'lfu') {
      const { capacity, operations } = sample;
      const data = new Map();
      const freq = new Map();
      const bucket = new Map();
      let minFreq = 0;
      const gets = [];
      const steps = [];

      const touch = (key) => {
        const f = freq.get(key);
        const set = bucket.get(f);
        set.delete(key);
        if (set.size === 0) {
          bucket.delete(f);
          if (minFreq === f) minFreq += 1;
        }

        const next = f + 1;
        freq.set(key, next);
        if (!bucket.has(next)) bucket.set(next, new Set());
        bucket.get(next).add(key);
      };

      const evict = () => {
        const set = bucket.get(minFreq);
        const key = set.values().next().value;
        set.delete(key);
        if (set.size === 0) bucket.delete(minFreq);
        data.delete(key);
        freq.delete(key);
        return key;
      };

      operations.forEach((op, idx) => {
        let action = '';
        let getResult = null;
        let evicted = null;

        if (op[0] === 1) {
          const [, key, value] = op;
          if (capacity === 0) {
            action = `PUT(${key}, ${value}) ignored because capacity is 0.`;
          } else if (data.has(key)) {
            data.set(key, value);
            touch(key);
            action = `PUT(${key}, ${value}) updates existing key and increases its frequency.`;
          } else {
            if (data.size >= capacity) evicted = evict();
            data.set(key, value);
            freq.set(key, 1);
            if (!bucket.has(1)) bucket.set(1, new Set());
            bucket.get(1).add(key);
            minFreq = 1;
            action = evicted !== null
              ? `PUT(${key}, ${value}) inserts key and evicts key ${evicted} with lowest frequency.`
              : `PUT(${key}, ${value}) inserts a new cache entry.`;
          }
        } else {
          const key = op[1];
          if (!data.has(key)) {
            gets.push(-1);
            getResult = -1;
            action = `GET(${key}) returns -1 because key is missing.`;
          } else {
            const value = data.get(key);
            gets.push(value);
            getResult = value;
            touch(key);
            action = `GET(${key}) returns ${value} and increases key frequency.`;
          }
        }

        const keysOrdered = Array.from(data.keys()).sort((a, b) => a - b);
        steps.push({
          mode: 'lfu',
          current: idx,
          operation: op,
          action,
          values: keysOrdered,
          front: keysOrdered.length ? 0 : -1,
          rear: keysOrdered.length - 1,
          getResult,
          cacheEntries: keysOrdered.map((key) => ({ key, value: data.get(key) })),
          outputs: [...gets],
          caption: action,
        });
      });

      return steps.length ? steps : [{ mode: 'lfu', values: [], front: -1, rear: -1, outputs: [], caption: 'No operations to simulate.' }];
    }

    const { values } = sample;
    const steps = [];
    const maxIndex = typeof sample.stopAtIndex === 'number'
      ? sample.stopAtIndex
      : values.length - 1;
    for (let i = 0; i <= maxIndex; i += 1) {
      steps.push({
        values,
        front: i,
        rear: values.length - 1,
        caption: i === maxIndex
          ? `Queue state reached the required result checkpoint at front index ${i}. Traversal stops here.`
          : `Queue state: front pointer at index ${i}, rear pointer at index ${values.length - 1}. Dequeue would remove from front while enqueue adds at rear.`,
      });
    }
    return steps;
  }

  if (visualType === 'dp' && sample.mode === 'coin-change') {
    const { coins, amount, expected } = sample;
    const dp = Array(amount + 1).fill(Infinity);
    dp[0] = 0;
    const steps = [{ idx: 0, dp: [0], caption: 'Base state: amount 0 needs 0 coins.' }];
    for (let value = 1; value <= amount; value += 1) {
      coins.forEach((coin) => {
        if (value - coin >= 0 && Number.isFinite(dp[value - coin])) {
          dp[value] = Math.min(dp[value], dp[value - coin] + 1);
        }
      });
      const visible = dp.slice(0, value + 1).map((entry) => (Number.isFinite(entry) ? entry : -1));
      const bestNow = Number.isFinite(dp[value]) ? dp[value] : -1;
      const matchedExpected = value === amount && bestNow === expected;
      steps.push({
        idx: value,
        dp: visible,
        caption: `Amount ${value}: minimum coins needed is ${bestNow}.`,
        matchedExpected,
      });
      if (matchedExpected) break;
    }
    return steps;
  }

  if (visualType === 'dp' && sample.mode === 'max-subarray') {
    const nums = sample.nums;
    let current = nums[0];
    let best = nums[0];
    const steps = [{
      mode: 'max-subarray',
      idx: 0,
      current,
      best,
      nums,
      dp: [current],
      caption: `Start with first element ${nums[0]}. current = best = ${nums[0]}.`,
    }];
    for (let i = 1; i < nums.length; i += 1) {
      current = Math.max(nums[i], current + nums[i]);
      best = Math.max(best, current);
      steps.push({
        mode: 'max-subarray',
        idx: i,
        current,
        best,
        nums,
        dp: nums.slice(0, i + 1),
        caption: `At index ${i}, decide between starting new subarray at ${nums[i]} or extending previous. current = ${current}, best = ${best}.`,
      });
    }
    return steps;
  }

  const { n } = sample;
  const dp = [0, 1, 2];
  const steps = [
    { idx: 1, dp: [1], caption: 'Base state: there is exactly 1 way to reach step 1.' },
    { idx: 2, dp: [1, 2], caption: 'Base state: there are exactly 2 ways to reach step 2.' },
  ];
  for (let i = 3; i <= n; i += 1) {
    dp[i] = dp[i - 1] + dp[i - 2];
    steps.push({
      idx: i,
      dp: dp.slice(1, i + 1),
      caption: `DP transition: ways(${i}) = ways(${i - 1}) + ways(${i - 2}) = ${dp[i - 1]} + ${dp[i - 2]} = ${dp[i]}.`,
    });
  }
  return steps;
};

const serializeForJava = (value) => {
  if (Array.isArray(value)) {
    if (value.length === 0) return 'new int[]{}';
    if (Array.isArray(value[0])) {
      if (value[0].every((entry) => typeof entry === 'string')) {
        return `new String[][]{${value.map((it) => `{${it.map((entry) => `\"${entry.replace(/\\/g, '\\\\').replace(/\"/g, '\\\"')}\"`).join(', ')}}`).join(', ')}}`;
      }
      return `new int[][]{${value.map((it) => `{${it.join(', ')}}`).join(', ')}}`;
    }
    if (value[0] !== undefined && typeof value[0] === 'string') {
      return `new String[]{${value.map((entry) => `\"${entry.replace(/\\/g, '\\\\').replace(/\"/g, '\\\"')}\"`).join(', ')}}`;
    }
    return `new int[]{${value.join(', ')}}`;
  }
  if (typeof value === 'string') return `\"${value.replace(/\\/g, '\\\\').replace(/\"/g, '\\\"')}\"`;
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
};

const buildJavaRunner = (problem, code) => {
  const testEntries = problem.question.tests.map((test, idx) => {
    const args = test.input.map((arg) => serializeForJava(arg)).join(', ');
    const expected = serializeForJava(test.expected);
    return `runTest(${idx}, Solution.${problem.question.functionName}(${args}), ${expected});`;
  }).join('\n    ');

  return `${code}

public class Main {
  private static boolean deepEquals(Object a, Object b) {
    if (a == null || b == null) return a == b;
    if (a instanceof int[] && b instanceof int[]) return java.util.Arrays.equals((int[]) a, (int[]) b);
    if (a instanceof int[][] && b instanceof int[][]) return java.util.Arrays.deepEquals((int[][]) a, (int[][]) b);
    return a.equals(b);
  }

  private static String stringify(Object val) {
    if (val instanceof int[]) return java.util.Arrays.toString((int[]) val);
    if (val instanceof int[][]) return java.util.Arrays.deepToString((int[][]) val);
    return String.valueOf(val);
  }

  private static void runTest(int index, Object actual, Object expected) {
    boolean ok = deepEquals(actual, expected);
    System.out.println("__RESULT__:" + index + ":" + (ok ? "1" : "0") + ":" + stringify(actual));
  }

  public static void main(String[] args) {
    ${testEntries}
  }
}`;
};

const buildJavaScriptRunner = (problem, code) => {
  const testsLiteral = JSON.stringify(problem.question.tests);
  return `${code}

const tests = ${testsLiteral};
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

tests.forEach((t, index) => {
  try {
    const actual = ${problem.question.functionName}(...t.input);
    const ok = same(actual, t.expected);
    console.log('__RESULT__:' + index + ':' + (ok ? '1' : '0') + ':' + JSON.stringify(actual));
  } catch (err) {
    console.log('__RESULT__:' + index + ':0:ERROR:' + err.message);
  }
});`;
};

const parseRunOutput = (stdout, testsCount) => {
  const lines = stdout.split('\n').filter((line) => line.startsWith('__RESULT__:'));
  const results = lines.map((line) => {
    const parts = line.split(':');
    return { index: Number(parts[1]), passed: parts[2] === '1', actual: parts.slice(3).join(':') };
  });
  if (results.length < testsCount) {
    const seen = new Set(results.map((r) => r.index));
    for (let i = 0; i < testsCount; i += 1) {
      if (!seen.has(i)) results.push({ index: i, passed: false, actual: 'No result' });
    }
  }
  return results.sort((a, b) => a.index - b.index);
};

const executeJavaScriptLocally = async (source) => {
  const logLines = [];
  const errorLines = [];
  const originalLog = console.log;
  const originalError = console.error;

  try {
    console.log = (...args) => {
      logLines.push(args.map((arg) => String(arg)).join(' '));
    };
    console.error = (...args) => {
      errorLines.push(args.map((arg) => String(arg)).join(' '));
    };

    // eslint-disable-next-line no-new-func
    const runner = new Function(source);
    runner();
  } catch (error) {
    errorLines.push(error?.stack || error?.message || String(error));
  } finally {
    console.log = originalLog;
    console.error = originalError;
  }

  return {
    run: {
      stdout: logLines.join('\n'),
      stderr: errorLines.join('\n'),
    },
  };
};

const executeCode = async (language, source) => {
  const configuredEndpoint = (process.env.REACT_APP_PISTON_EXECUTE_URL || '').trim();
  const endpoints = [configuredEndpoint, 'https://emkc.org/api/v2/piston/execute'].filter(Boolean);
  let lastError = null;

  for (let i = 0; i < endpoints.length; i += 1) {
    const endpoint = endpoints[i];
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ language, version: '*', files: [{ content: source }] }),
      });

      if (!response.ok) {
        let errorMessage = '';
        try {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const body = await response.json();
            errorMessage = body?.message || body?.error || '';
          } else {
            errorMessage = await response.text();
          }
        } catch (_) {
          errorMessage = '';
        }

        const whitelistBlocked = response.status === 401 && /whitelist only/i.test(errorMessage);
        if (whitelistBlocked) {
          if (language === 'javascript') {
            return executeJavaScriptLocally(source);
          }
          throw new Error('Code execution is blocked: public Piston API is now whitelist-only. Configure REACT_APP_PISTON_EXECUTE_URL to your own Piston server and redeploy.');
        }

        throw new Error(errorMessage || `Execution failed with status ${response.status}`);
      }

      return response.json();
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error('Execution failed. No runner endpoint is reachable.');
};

const ProblemDetailPage = () => {
  const { slug } = useParams();
  const location = useLocation();
  const { currentUser } = useAuth();
  const problem = useMemo(() => getProblemById(slug), [slug]);
  const backToProblems = `/problems${location.state?.fromSearch || ''}`;

  const [language, setLanguage] = useState('javascript');
  const [code, setCode] = useState(problem?.question.jsStarter || '');
  const [results, setResults] = useState([]);
  const [runError, setRunError] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [showVisualizer, setShowVisualizer] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [problemPaneWidth, setProblemPaneWidth] = useState(47);
  const [testPaneHeight, setTestPaneHeight] = useState(220);
  const [testsCollapsed, setTestsCollapsed] = useState(false);
  const [activeTestCase, setActiveTestCase] = useState(0);
  const [progress, setProgress] = useState({ solved: [] });

  const workspaceRef = useRef(null);
  const editorPaneRef = useRef(null);
  const dragRef = useRef(null);

  const userProgressKey = currentUser ? `user_${currentUser.id}` : 'guest';
  const hasConfiguredRunner = Boolean((process.env.REACT_APP_PISTON_EXECUTE_URL || '').trim());
  const hasInteractiveContent = Boolean(problem?.interactive
    && problem?.question?.tests?.length
    && problem?.question?.jsStarter);

  useEffect(() => {
    const loadProgress = async () => {
      const local = readLocalProgress(userProgressKey);
      setProgress(local);
      if (!currentUser) return;
      try {
        const snap = await getDoc(doc(db, 'dsaProgress', userProgressKey));
        if (snap.exists()) setProgress({ solved: snap.data().solved || [] });
      } catch (_) {
        // fallback local
      }
    };
    loadProgress();
  }, [currentUser, userProgressKey]);

  useEffect(() => {
    if (!problem) return;
    setCode(hasInteractiveContent
      ? (language === 'javascript' ? problem.question.jsStarter : problem.question.javaStarter)
      : '');
    setResults([]);
    setRunError('');
    setActiveTestCase(0);
    setStepIndex(0);
    setIsPlaying(false);
  }, [problem, language, hasInteractiveContent]);

  const visualizationSample = useMemo(() => {
    if (!problem || !hasInteractiveContent) return null;
    return normalizeSample(problem.visualType, inferSampleFromProblem(problem, problem.visualType));
  }, [problem, hasInteractiveContent]);

  const steps = useMemo(() => (problem && hasInteractiveContent ? buildVisualizationSteps(problem) : []), [problem, hasInteractiveContent]);
  const currentStep = steps[Math.min(stepIndex, Math.max(steps.length - 1, 0))];

  useEffect(() => {
    if (!isPlaying || steps.length <= 1 || !showVisualizer) return undefined;
    const intervalId = window.setInterval(() => {
      setStepIndex((prev) => {
        const lastIndex = steps.length - 1;
        if (prev >= lastIndex) {
          setIsPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, 1200);
    return () => window.clearInterval(intervalId);
  }, [isPlaying, steps.length, showVisualizer]);

  const onHorizontalSplitMouseDown = useCallback((event) => {
    event.preventDefault();
    dragRef.current = { type: 'horizontal' };
    document.body.style.cursor = 'col-resize';
  }, []);

  const onVerticalSplitMouseDown = useCallback((event) => {
    event.preventDefault();
    dragRef.current = { type: 'vertical' };
    document.body.style.cursor = 'row-resize';
  }, []);

  useEffect(() => {
    const onMouseMove = (event) => {
      if (!dragRef.current) return;
      if (dragRef.current.type === 'horizontal' && workspaceRef.current) {
        const rect = workspaceRef.current.getBoundingClientRect();
        setProblemPaneWidth(clamp(((event.clientX - rect.left) / rect.width) * 100, 28, 72));
      }
      if (dragRef.current.type === 'vertical' && editorPaneRef.current) {
        const rect = editorPaneRef.current.getBoundingClientRect();
        const nextHeight = rect.bottom - event.clientY;
        setTestPaneHeight(clamp(nextHeight, 120, rect.height - 160));
      }
    };

    const onMouseUp = () => {
      dragRef.current = null;
      document.body.style.cursor = '';
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const persistSolved = async () => {
    if (!problem) return;
    if (progress.solved.includes(problem.id)) return;
    const next = { solved: [...progress.solved, problem.id] };
    setProgress(next);
    writeLocalProgress(userProgressKey, next);
    if (!currentUser) return;
    try {
      await setDoc(doc(db, 'dsaProgress', userProgressKey), {
        solved: next.solved,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (_) {
      // fallback local already saved
    }
  };

  const handleRunTests = async () => {
    if (!problem) return;
    setIsRunning(true);
    setRunError('');
    setResults([]);

    try {
      if (language === 'java' && !hasConfiguredRunner) {
        throw new Error('Java execution is disabled. Use JavaScript for free in-browser execution, or configure REACT_APP_PISTON_EXECUTE_URL for Java.');
      }
      const runner = language === 'javascript'
        ? buildJavaScriptRunner(problem, code)
        : buildJavaRunner(problem, code);
      const response = await executeCode(language, runner);
      if (response.run && response.run.stderr && !(response.run.stdout || '').includes('__RESULT__:')) {
        throw new Error(response.run.stderr);
      }
      const parsed = parseRunOutput(response.run.stdout || '', problem.question.tests.length);
      setResults(parsed);
      if (parsed.length > 0 && parsed.every((entry) => entry.passed)) await persistSolved();
    } catch (err) {
      setRunError(err.message || 'Execution failed.');
    } finally {
      setIsRunning(false);
    }
  };

  if (!problem) {
    return (
      <div className="problem-detail-page">
        <div className="gradient__bg"><Navbar /></div>
        <section className="problem-not-found">
          <h1>Problem Not Found</h1>
          <p>The requested problem does not exist.</p>
          <Link to="/problems">Go Back to Problem List</Link>
        </section>
        <Footer />
      </div>
    );
  }

  const resultByCase = Object.fromEntries(results.map((entry) => [entry.index, entry]));
  const activeCase = problem.question.tests[activeTestCase] || problem.question.tests[0] || null;
  const renderExamples = (problem.question.examples?.length ? problem.question.examples : problem.question.tests.map((test, idx) => ({
    title: `Example ${idx + 1}`,
    input: JSON.stringify(test.input),
    output: JSON.stringify(test.expected),
    explanation: 'Verify the input-output relationship carefully and ensure all edge cases are handled.',
  })));
  const explanationText = {
    'array-pointer': 'This visualizer shows one iterator moving left-to-right while using a hash map for O(1) complement checks. Arrow markers reveal current index i and the matching index when found.',
    'binary-search': 'This visualizer shows the shrinking search interval with left, right, and mid arrows. Each step discards one half of the array based on comparison with target.',
    stack: 'This visualizer shows character scanning with push/pop stack operations. Active character is highlighted and stack contents evolve with each token.',
    window: 'This visualizer shows moving buy/sell pointers. Sell pointer scans each day while buy pointer tracks the lowest price box seen so far.',
    intervals: 'This visualizer shows sorted intervals and highlights the current interval being merged into the result set.',
    dp: 'This visualizer shows state build-up where each DP value depends on previous computed values.',
    tree: 'This visualizer runs trie/tree operations step-by-step, showing each query and the running output value until final expected answers are reached.',
    graph: 'This visualizer follows BFS/DFS or topological processing per testcase, shows what state changes each step, and stops at the target output condition.',
    'linked-list': 'This visualizer shows a pointer moving through linked nodes one-by-one.',
    queue: 'This visualizer shows front and rear movement while processing queue state.',
  };
  const resolvedExplanationText = (() => {
    if (problem.visualType === 'stack' && problem.question.functionName === 'dailyTemperatures') {
      return 'This visualizer tracks day index and temperature while maintaining a monotonic stack. Final output is the wait-days array, not the current day value.';
    }
    if (problem.question.functionName === 'moveZeroesStable') {
      return 'This visualizer shows read/write pointers. Non-zero values move forward in stable order while zeros are pushed to the end.';
    }
    if (problem.question.functionName === 'searchInsert') {
      return 'This visualizer shows binary-search narrowing and highlights either exact target index or final insertion index.';
    }
    if (problem.question.functionName === 'maxSubArrayKadane') {
      return 'This visualizer shows Kadane updates: current best subarray ending here and global best seen so far.';
    }
    return explanationText[problem.visualType];
  })();
  const visualizerWorkedExample = (() => {
    const firstTest = problem.question.tests?.[0];
    if (!firstTest) return '';

    if ((problem.visualType === 'array-pointer' || problem.visualType === 'binary-search') && visualizationSample?.nums) {
      if (typeof visualizationSample.target === 'number') {
        return `Example in this visualizer: input [${JSON.stringify(visualizationSample.nums)}, ${visualizationSample.target}] produces output ${JSON.stringify(firstTest.expected)}.`;
      }
      return `Example in this visualizer: input ${JSON.stringify(firstTest.input)} produces output ${JSON.stringify(firstTest.expected)}.`;
    }

    if (problem.visualType === 'stack' && visualizationSample?.text) {
      return `Example in this visualizer: input ${JSON.stringify([visualizationSample.text])} produces output ${JSON.stringify(firstTest.expected)}.`;
    }

    if (problem.visualType === 'stack' && visualizationSample?.mode === 'daily-temperatures' && visualizationSample?.temps) {
      return `Example in this visualizer: input ${JSON.stringify([visualizationSample.temps])} produces output ${JSON.stringify(firstTest.expected)}.`;
    }

    if (problem.visualType === 'window' && visualizationSample?.prices) {
      return `Example in this visualizer: input ${JSON.stringify([visualizationSample.prices])} produces output ${JSON.stringify(firstTest.expected)}.`;
    }

    if (problem.visualType === 'intervals' && visualizationSample?.intervals) {
      return `Example in this visualizer: input ${JSON.stringify([visualizationSample.intervals])} produces output ${JSON.stringify(firstTest.expected)}.`;
    }

    if (problem.visualType === 'dp' && typeof visualizationSample?.n === 'number') {
      return `Example in this visualizer: input ${JSON.stringify([visualizationSample.n])} produces output ${JSON.stringify(firstTest.expected)}.`;
    }

    if (problem.visualType === 'dp' && visualizationSample?.mode === 'coin-change') {
      return `Example in this visualizer: input ${JSON.stringify([visualizationSample.coins, visualizationSample.amount])} produces output ${JSON.stringify(firstTest.expected)}.`;
    }

    if (problem.visualType === 'binary-search' && visualizationSample?.mode === 'find-min-rotated' && visualizationSample?.nums) {
      return `Example in this visualizer: input ${JSON.stringify([visualizationSample.nums])} produces output ${JSON.stringify(firstTest.expected)}.`;
    }

    if (problem.visualType === 'linked-list' && visualizationSample?.mode === 'add-two-numbers') {
      return `Example in this visualizer: input ${JSON.stringify([visualizationSample.l1, visualizationSample.l2])} produces output ${JSON.stringify(firstTest.expected)}.`;
    }

    if (problem.visualType === 'queue' && visualizationSample?.mode === 'lfu') {
      return `Example in this visualizer: input ${JSON.stringify([visualizationSample.capacity, visualizationSample.operations])} produces output ${JSON.stringify(firstTest.expected)}.`;
    }

    if ((problem.visualType === 'linked-list' || problem.visualType === 'queue') && visualizationSample?.values) {
      return `Example in this visualizer: input ${JSON.stringify(firstTest.input)} produces output ${JSON.stringify(firstTest.expected)}.`;
    }

    return `Example in this visualizer: input ${JSON.stringify(firstTest.input)} produces output ${JSON.stringify(firstTest.expected)}.`;
  })();

  const visualizerMomentText = (() => {
    if (!currentStep) return '';

    if (problem.visualType === 'array-pointer' && currentStep.nums) {
      if (currentStep.mode === 'move-zeroes') {
        return `Read pointer at index ${currentStep.current}. Write boundary is ${currentStep.write}. Array state is ${JSON.stringify(currentStep.nums)}.`;
      }
      if (problem.question.functionName === 'threeSum') {
        const anchor = currentStep.anchor ?? 0;
        const left = currentStep.current ?? 0;
        const right = currentStep.match ?? left;
        const a = currentStep.nums[anchor];
        const b = currentStep.nums[left];
        const c = currentStep.nums[right];
        const total = a + b + c;
        return `Current triplet is (${a}, ${b}, ${c}) at indices (${anchor}, ${left}, ${right}) with sum ${total}.`;
      }

      if (problem.question.functionName === 'findKthLargest') {
        const idx = currentStep.current ?? 0;
        const value = currentStep.nums[idx];
        return `Descending pointer is at index ${idx} with value ${value}. Stop when index reaches k - 1.`;
      }

      const i = currentStep.current ?? 0;
      const currentValue = currentStep.nums[i];
      const target = currentStep.target;
      const needed = target - currentValue;
      if (typeof currentStep.match === 'number' && currentStep.match >= 0) {
        return `Current element is ${currentValue} at index ${i}. Needed value is ${needed}, found at index ${currentStep.match}. Pair [${currentStep.match}, ${i}] is complete, so the loop stops.`;
      }
      return `Current element is ${currentValue} at index ${i}. Needed value is ${needed}. It is not found yet, so we store this index and move to the next element.`;
    }

    if (problem.visualType === 'binary-search' && currentStep.mode === 'find-min-rotated' && currentStep.nums) {
      const midVal = currentStep.nums[currentStep.mid];
      if (currentStep.found) {
        return `Left and right converged at index ${currentStep.left}, so minimum value is ${midVal}.`;
      }
      return `Window is [${currentStep.left}, ${currentStep.right}] with mid ${currentStep.mid} (value ${midVal}). Compare with right boundary to keep the half that must contain the minimum.`;
    }

    if (problem.visualType === 'binary-search' && currentStep.mode === 'search-insert' && currentStep.nums) {
      if (currentStep.caption?.includes('Insertion index')) {
        return `Target ${currentStep.target} not found. Correct insertion index is ${currentStep.left}.`;
      }
      return `Window is [${currentStep.left}, ${currentStep.right}], mid is ${currentStep.mid}, value is ${currentStep.nums[currentStep.mid]}.`;
    }

    if (problem.visualType === 'binary-search' && currentStep.nums) {
      if (currentStep.mode === 'median') {
        if (typeof currentStep.match === 'number' && currentStep.match >= 0) {
          return `Merged order is shown in the boxes. Median comes from middle indices ${currentStep.mid} and ${currentStep.match}, and their average is returned.`;
        }
        return `Merged order is shown in the boxes. Median is the middle index ${currentStep.mid}, so that value is returned.`;
      }
      const leftVal = currentStep.nums[currentStep.left];
      const rightVal = currentStep.nums[currentStep.right];
      const midVal = currentStep.nums[currentStep.mid];
      if (currentStep.found) {
        return `Search window [${currentStep.left}, ${currentStep.right}] with mid ${currentStep.mid} gives value ${midVal}. Target is matched here, so the search stops.`;
      }
      return `Search window is [${currentStep.left}, ${currentStep.right}] with values [${leftVal}, ${rightVal}]. Mid is ${currentStep.mid} (value ${midVal}), so one half is discarded and the loop continues.`;
    }

    if (problem.visualType === 'stack' && currentStep.mode === 'daily-temperatures' && currentStep.temps) {
      const day = currentStep.current ?? 0;
      const wait = currentStep.output?.[day] ?? 0;
      const targetOutput = JSON.stringify(visualizationSample?.expected || []);
      const currentOutput = JSON.stringify(currentStep.output || []);
      return `Current day pointer is index ${day} and its temperature is ${currentStep.temps[day]}. These are traversal values, not final answer values. Current computed output array is ${currentOutput}; target output array is ${targetOutput}. Wait-days value at current index is ${wait}.`;
    }

    if (problem.visualType === 'stack' && currentStep.text) {
      const ch = currentStep.text[currentStep.current];
      return `Current character is '${ch}'. The structure state is [${(currentStep.stack || []).join(', ')}] after processing this character.`;
    }

    if (problem.visualType === 'window' && currentStep.prices) {
      if (currentStep.mode === 'container') {
        return `Left pointer is at index ${currentStep.minIdx}, right pointer is at index ${currentStep.current}. Current area is ${currentStep.area}, best area so far is ${currentStep.best}.`;
      }
      const sellIdx = currentStep.current;
      const sellPrice = currentStep.prices[sellIdx];
      return `Sell pointer is at index ${sellIdx} (price ${sellPrice}). Best buy so far is index ${currentStep.minIdx} (price ${currentStep.minPrice}), and best profit is ${currentStep.best}.`;
    }

    if (problem.visualType === 'window' && currentStep.chars) {
      const windowString = currentStep.chars.slice(currentStep.left, currentStep.current + 1).join('');
      return `Left pointer is ${currentStep.left}, right pointer is ${currentStep.current}, and current substring window is "${windowString}". Best length so far is ${currentStep.best}.`;
    }

    if (problem.visualType === 'intervals' && currentStep.sorted) {
      const active = currentStep.sorted[currentStep.current];
      return active
        ? `Active interval is [${active[0]}, ${active[1]}]. Merged result so far is ${JSON.stringify(currentStep.merged || [])}.`
        : `Initial sorted interval is ${JSON.stringify(currentStep.sorted[0])}.`;
    }

    if (problem.visualType === 'dp' && currentStep.dp) {
      if (currentStep.mode === 'max-subarray') {
        return `Index ${currentStep.idx}: current subarray sum is ${currentStep.current}, best overall sum is ${currentStep.best}.`;
      }
      return `At state ${currentStep.idx}, computed values so far are ${JSON.stringify(currentStep.dp)}.`;
    }

    if (problem.visualType === 'tree' && currentStep.activeId) {
      if (currentStep.mode === 'trie-prefix') {
        return `Active trie query is "${currentStep.query}". Current matched count is ${currentStep.count}, expected at this query is ${currentStep.expectedValue}.`;
      }
      return `Current active tree node is ${currentStep.activeId}. Traversal proceeds to the next connected child/branch in the following step.`;
    }

    if (problem.visualType === 'graph' && currentStep.activeId) {
      if (currentStep.mode === 'course-schedule') {
        return `Course ${currentStep.activeId} is being processed. Completed ${currentStep.taken}/${currentStep.total} courses; pending queue is [${(currentStep.queueSnapshot || []).join(', ')}].`;
      }
      if (currentStep.mode === 'word-ladder') {
        return `Current word is "${currentStep.activeId}" at ladder distance ${currentStep.distance}.`;
      }
      if (currentStep.mode === 'num-islands') {
        return `Currently visiting cell ${currentStep.activeId}. Islands counted so far: ${currentStep.islands}.`;
      }
      if (currentStep.mode === 'shortest-path-matrix') {
        return `Currently visiting cell ${currentStep.activeId} at distance ${currentStep.distance}.`;
      }
      return `Current active graph node is ${currentStep.activeId}. Neighbor exploration continues through connected edges.`;
    }

    if (problem.visualType === 'linked-list' && currentStep.mode === 'add-two-numbers') {
      return `Position ${currentStep.current}: add ${currentStep.a} and ${currentStep.b} with incoming carry. Output digit is ${currentStep.digit}, next carry is ${currentStep.carry}. Partial result is ${JSON.stringify(currentStep.output)}.`;
    }

    if (problem.visualType === 'linked-list' && currentStep.values) {
      const idx = currentStep.current ?? 0;
      const stopIdx = typeof visualizationSample?.stopAtIndex === 'number' ? visualizationSample.stopAtIndex : null;
      if (stopIdx !== null && idx >= stopIdx) {
        return `Current node is index ${idx} with value ${currentStep.values[idx]}. Expected output is satisfied at this point, so pointer stops here.`;
      }
      return `Current node is index ${idx} with value ${currentStep.values[idx]}. Then pointer moves to the next node.`;
    }

    if (problem.visualType === 'queue' && currentStep.mode === 'lfu') {
      const op = currentStep.operation || [];
      return `Operation ${currentStep.current + 1}: ${JSON.stringify(op)}. ${currentStep.action} Current cache keys are [${(currentStep.values || []).join(', ')}], and get outputs so far are ${JSON.stringify(currentStep.outputs || [])}.`;
    }

    if (problem.visualType === 'queue' && currentStep.values) {
      const frontVal = currentStep.values[currentStep.front];
      const rearVal = currentStep.values[currentStep.rear];
      const stopIdx = typeof visualizationSample?.stopAtIndex === 'number' ? visualizationSample.stopAtIndex : null;
      if (stopIdx !== null && currentStep.front >= stopIdx) {
        return `Front is index ${currentStep.front} (value ${frontVal}) and required output checkpoint is reached, so processing stops here.`;
      }
      return `Front is index ${currentStep.front} (value ${frontVal}) and rear is index ${currentStep.rear} (value ${rearVal}). Processing advances from the front side.`;
    }

    return currentStep.caption || '';
  })();

  return (
    <div className="problem-detail-page">
      <SEOMeta
        title={`${problem.title} | Algodoor DSA`}
        description={`Practice ${problem.title} with visualizer and testcase runner.`}
        url={`/problem/${problem.id}`}
        type="WebPage"
      />

      <div className="gradient__bg"><Navbar /></div>

      <section className="problem-detail-header">
        <Link to={backToProblems} className="back-link">← Back to All Problems</Link>
        <h1>{problem.title}</h1>
        <p>{hasInteractiveContent ? 'Solve the problem below and validate your solution with the test cases.' : 'This imported catalog entry shows verified metadata only, so unrelated in-app solutions and test cases are intentionally hidden.'}</p>
      </section>

      {hasInteractiveContent ? <section className="visualizer-accordion-wrap">
        <button type="button" className="visualizer-toggle" onClick={() => setShowVisualizer((prev) => !prev)}>
          {showVisualizer ? 'Hide Visualizer' : 'See Visualizer'}
        </button>

        {showVisualizer && (
          <div className="visualizer-content">
            {visualizerWorkedExample ? <p className="visualizer-example-note">{visualizerWorkedExample}</p> : null}
            {problem.visualType === 'stack' && currentStep?.mode === 'daily-temperatures' ? (
              <p className="visualizer-example-note">
                {`Traversal state: day index = ${currentStep.current}, temperature = ${currentStep.temps?.[currentStep.current]}. Current output array = ${JSON.stringify(currentStep.output || [])}. Target output array = ${JSON.stringify(visualizationSample?.expected || [])}.`}
              </p>
            ) : null}
            <div className="visual-stage">
              {problem.visualType === 'array-pointer' || problem.visualType === 'binary-search' ? (
                <div className="dsa-array-wrap">
                  {currentStep.nums?.map((num, idx) => {
                    const isCurrent = idx === currentStep.current || idx === currentStep.mid;
                    const isFocus = idx === currentStep.match || idx === currentStep.left || idx === currentStep.right;
                    const labels = [];
                    if (idx === currentStep.current) labels.push('i');
                    if (idx === currentStep.mid) labels.push('mid');
                    if (idx === currentStep.left) labels.push('left');
                    if (idx === currentStep.right) labels.push('right');
                    if (idx === currentStep.match) labels.push('match');
                    return (
                      <div key={`${problem.id}-${idx}`} className={`dsa-box ${isCurrent ? 'current' : ''} ${isFocus ? 'focus' : ''}`}>
                        <div className="arrow-marker-wrap">
                          {labels.map((label) => (
                            <span key={`${problem.id}-${idx}-${label}`} className="arrow-marker">↓ {label}</span>
                          ))}
                        </div>
                        <span className="dsa-index">{idx}</span>
                        <span>{num}</span>
                      </div>
                    );
                  })}
                </div>
              ) : null}

              {problem.visualType === 'stack' ? (
                <div className="dsa-stack-layout">
                  {currentStep.mode === 'daily-temperatures' ? (
                    <>
                      <div className="dsa-array-wrap">
                        {currentStep.temps?.map((temp, idx) => (
                          <div key={`${problem.id}-t-${idx}`} className={`dsa-box ${idx === currentStep.current ? 'current' : ''}`}>
                            <div className="arrow-marker-wrap">
                              {idx === currentStep.current ? <span className="arrow-marker">↓ day</span> : null}
                            </div>
                            <span className="dsa-index">{idx}</span>
                            <span>{temp}</span>
                          </div>
                        ))}
                      </div>
                      <div className="dsa-stack">
                        {(currentStep.stack || []).map((index, idx) => (
                          <div key={`stack-${index}-${idx}`} className="dsa-stack-item">idx {index} ({currentStep.temps?.[index]})</div>
                        ))}
                      </div>
                      <div className="dsa-array-wrap">
                        {(currentStep.output || []).map((days, idx) => (
                          <div key={`${problem.id}-o-${idx}`} className={`dsa-box ${idx === currentStep.current ? 'current' : ''}`}>
                            <div className="arrow-marker-wrap">
                              {idx === currentStep.current ? <span className="arrow-marker">↓ out</span> : null}
                            </div>
                            <span className="dsa-index">{idx}</span>
                            <span>{days}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="dsa-char-row">
                        {currentStep.text?.split('').map((ch, idx) => (
                          <div key={`${ch}-${idx}`} className={`dsa-char-box ${idx === currentStep.current ? 'active' : ''}`}>{ch}</div>
                        ))}
                      </div>
                      <div className="dsa-stack">
                        {(currentStep.stack || []).map((item, idx) => <div key={`${item}-${idx}`} className="dsa-stack-item">{item}</div>)}
                      </div>
                    </>
                  )}
                </div>
              ) : null}

              {problem.visualType === 'window' ? (
                <div className="dsa-array-wrap">
                  {currentStep.prices?.map((num, idx) => (
                    <div key={`${problem.id}-${idx}`} className={`dsa-box ${idx === currentStep.current ? 'current' : ''}`}>
                      <div className="arrow-marker-wrap">
                        {idx === currentStep.current ? <span className="arrow-marker">↓ sell</span> : null}
                        {idx === currentStep.minIdx ? <span className="arrow-marker buy-arrow">↓ buy</span> : null}
                      </div>
                      <span className="dsa-index">{idx}</span>
                      <span>{num}</span>
                    </div>
                  ))}

                  {currentStep.chars?.map((ch, idx) => (
                    <div key={`${problem.id}-ch-${idx}`} className={`dsa-box ${idx === currentStep.current ? 'current' : ''} ${idx >= currentStep.left && idx <= currentStep.current ? 'focus' : ''}`}>
                      <div className="arrow-marker-wrap">
                        {idx === currentStep.current ? <span className="arrow-marker">↓ right</span> : null}
                        {idx === currentStep.left ? <span className="arrow-marker buy-arrow">↓ left</span> : null}
                      </div>
                      <span className="dsa-index">{idx}</span>
                      <span>{ch}</span>
                    </div>
                  ))}
                </div>
              ) : null}

              {problem.visualType === 'intervals' ? (
                <div className="dsa-intervals-layout">
                  <div className="interval-track">
                    {currentStep.sorted?.map((interval, idx) => (
                      <div key={`sorted-${idx}`} className={`interval-chip ${idx === currentStep.current ? 'active' : ''}`}>
                        [{interval[0]}, {interval[1]}]
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {problem.visualType === 'dp' ? (
                <div className="dsa-array-wrap">
                  {currentStep.dp?.map((value, idx) => (
                    <div key={`${value}-${idx}`} className={`dsa-box ${idx + 1 === currentStep.idx ? 'current' : ''}`}>
                      <span className="dsa-index">step {idx + 1}</span>
                      <span>{value}</span>
                    </div>
                  ))}
                </div>
              ) : null}

              {problem.visualType === 'tree' ? (
                <div className="network-stage">
                  <svg viewBox="0 0 100 100" className="network-svg" aria-label="Tree visualizer">
                    {currentStep.edges?.map(([from, to]) => {
                      const fromNode = currentStep.nodes.find((n) => n.id === from);
                      const toNode = currentStep.nodes.find((n) => n.id === to);
                      return (
                        <line
                          key={`${from}-${to}`}
                          x1={fromNode.x}
                          y1={fromNode.y}
                          x2={toNode.x}
                          y2={toNode.y}
                          className="network-edge"
                        />
                      );
                    })}
                    {currentStep.nodes?.map((node) => (
                      <g key={node.id}>
                        <circle
                          cx={node.x}
                          cy={node.y}
                          r="6"
                          className={`network-node ${currentStep.activeId === node.id ? 'active' : ''}`}
                        />
                        <text x={node.x} y={node.y + 1.8} textAnchor="middle" className="network-label">
                          {node.label}
                        </text>
                      </g>
                    ))}
                  </svg>
                </div>
              ) : null}

              {problem.visualType === 'graph' ? (
                <div className="network-stage">
                  <svg viewBox="0 0 100 100" className="network-svg" aria-label="Graph visualizer">
                    {currentStep.edges?.map(([from, to]) => {
                      const fromNode = currentStep.nodes.find((n) => n.id === from);
                      const toNode = currentStep.nodes.find((n) => n.id === to);
                      return (
                        <line
                          key={`${from}-${to}`}
                          x1={fromNode.x}
                          y1={fromNode.y}
                          x2={toNode.x}
                          y2={toNode.y}
                          className="network-edge"
                        />
                      );
                    })}
                    {currentStep.nodes?.map((node) => (
                      <g key={node.id}>
                        <circle
                          cx={node.x}
                          cy={node.y}
                          r="6"
                          className={`network-node rounded ${currentStep.activeId === node.id ? 'active' : ''}`}
                        />
                        <text x={node.x} y={node.y + 1.8} textAnchor="middle" className="network-label">
                          {node.label || node.id}
                        </text>
                      </g>
                    ))}
                  </svg>
                </div>
              ) : null}

              {problem.visualType === 'linked-list' ? (
                <div className="linked-stage">
                  {currentStep.mode === 'add-two-numbers' ? currentStep.output?.map((value, idx) => (
                    <React.Fragment key={`add-${value}-${idx}`}>
                      <div className={`linked-node ${idx === currentStep.current ? 'active' : ''}`}>
                        {idx === currentStep.current ? <span className="node-pointer">↓ digit</span> : null}
                        <span>{value}</span>
                      </div>
                      {idx < currentStep.output.length - 1 ? <span className="node-link">→</span> : null}
                    </React.Fragment>
                  )) : currentStep.values?.map((value, idx) => (
                    <React.Fragment key={`ll-${value}-${idx}`}>
                      <div className={`linked-node ${idx === currentStep.current ? 'active' : ''}`}>
                        {idx === currentStep.current ? <span className="node-pointer">↓ curr</span> : null}
                        <span>{value}</span>
                      </div>
                      {idx < currentStep.values.length - 1 ? <span className="node-link">→</span> : null}
                    </React.Fragment>
                  ))}
                </div>
              ) : null}

              {problem.visualType === 'queue' ? (
                <div className="linked-stage queue-stage">
                  {currentStep.mode === 'lfu' ? currentStep.cacheEntries?.map((entry, idx) => (
                    <React.Fragment key={`lfu-${entry.key}-${idx}`}>
                      <div className={`linked-node ${idx === currentStep.front || idx === currentStep.rear ? 'active' : ''}`}>
                        {idx === currentStep.front ? <span className="node-pointer">↓ front</span> : null}
                        {idx === currentStep.rear ? <span className="node-pointer rear">↓ rear</span> : null}
                        <span>{entry.key}:{entry.value}</span>
                      </div>
                      {idx < currentStep.cacheEntries.length - 1 ? <span className="node-link">→</span> : null}
                    </React.Fragment>
                  )) : currentStep.values?.map((value, idx) => (
                    <React.Fragment key={`q-${value}-${idx}`}>
                      <div className={`linked-node ${idx === currentStep.front || idx === currentStep.rear ? 'active' : ''}`}>
                        {idx === currentStep.front ? <span className="node-pointer">↓ front</span> : null}
                        {idx === currentStep.rear ? <span className="node-pointer rear">↓ rear</span> : null}
                        <span>{value}</span>
                      </div>
                      {idx < currentStep.values.length - 1 ? <span className="node-link">→</span> : null}
                    </React.Fragment>
                  ))}
                </div>
              ) : null}
            </div>

            {visualizerMomentText ? <p className="visualizer-moment-note">{visualizerMomentText}</p> : null}
            <p className="visual-caption">{currentStep?.caption || 'Visualizer ready.'}</p>
            <div className="player-controls">
              <button type="button" onClick={() => setIsPlaying((prev) => !prev)}>{isPlaying ? 'Pause' : 'Play'}</button>
              <button type="button" onClick={() => setStepIndex((prev) => Math.max(prev - 1, 0))}>Previous</button>
              <button type="button" onClick={() => setStepIndex((prev) => Math.min(prev + 1, Math.max(steps.length - 1, 0)))}>Next</button>
            </div>
          </div>
        )}
      </section> : null}

      <section className="leetcode-workspace-section">
        <div className="leetcode-workspace" ref={workspaceRef}>
          <article className="problem-pane" style={{ width: `${problemPaneWidth}%` }}>
            <div className="problem-headline">
              <h3>{problem.question.title}</h3>
              <span className="difficulty-chip">{problem.difficulty}</span>
            </div>
            <div className="problem-statement" dangerouslySetInnerHTML={{ __html: problem.question.statement }} />
            <h4>Description</h4>
            <div className="problem-description" dangerouslySetInnerHTML={{ __html: problem.question.description }} />
            {hasInteractiveContent ? <p className="explanation-note">{resolvedExplanationText}</p> : null}
            <h4>Examples</h4>
            <div className="problem-examples">
              {renderExamples.length ? renderExamples.map((example, idx) => (
                <div key={`example-${idx}`} className="problem-example-card">
                  <h5>{example.title || `Example ${idx + 1}`}</h5>
                  <pre className="example-code-block">
                    {`Input: ${example.input}\nOutput: ${example.output}`}
                  </pre>
                  <p><strong>Explanation:</strong> {example.explanation}</p>
                </div>
              )) : <div className="problem-example-card"><p>Verified sample input/output has not been imported for this online entry yet. This is hidden intentionally to avoid showing the wrong example or wrong solution.</p></div>}
            </div>
            <h4>Constraints</h4>
            <ul className="constraint-list">
              {problem.question.constraints.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </article>

          <div className="workspace-splitter vertical" onMouseDown={onHorizontalSplitMouseDown} />

          <article className="editor-pane" ref={editorPaneRef}>
            {hasInteractiveContent ? <>
            <div className="runner-topbar leetcode-topbar">
              <label htmlFor="language-select">Language</label>
              <select id="language-select" value={language} onChange={(event) => setLanguage(event.target.value)}>
                <option value="javascript">JavaScript</option>
                <option value="java" disabled={!hasConfiguredRunner}>Java{hasConfiguredRunner ? '' : ' (configure runner first)'}</option>
              </select>
              <button type="button" onClick={handleRunTests} disabled={isRunning}>{isRunning ? 'Running...' : 'Run Test Cases'}</button>
            </div>
            {!hasConfiguredRunner ? <p className="explanation-note">Free mode is enabled: JavaScript runs locally in-browser. Java requires a configured runner endpoint (REACT_APP_PISTON_EXECUTE_URL).</p> : null}

            <div className="editor-body">
              <div className="code-editor-monaco" role="region" aria-label="Code editor">
                <Editor
                  height="100%"
                  language={language === 'javascript' ? 'javascript' : 'java'}
                  theme="vs-dark"
                  value={code}
                  onChange={(value) => setCode(value || '')}
                  options={{
                    minimap: { enabled: false },
                    fontSize: 14,
                    lineNumbers: 'on',
                    smoothScrolling: true,
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    tabSize: 2,
                  }}
                />
              </div>
            </div>

            {!testsCollapsed ? <div className="workspace-splitter horizontal" onMouseDown={onVerticalSplitMouseDown} /> : null}

            <div className={`testcase-pane ${testsCollapsed ? 'collapsed' : ''}`} style={{ height: testsCollapsed ? 42 : `${testPaneHeight}px` }}>
              <div className="testcase-pane-header">
                <div className="testcase-pane-title-wrap">
                  <strong>Testcases</strong>
                  <span>{results.length ? `${results.filter((r) => r.passed).length}/${results.length} passed` : 'Not executed'}</span>
                </div>
                <button type="button" onClick={() => setTestsCollapsed((prev) => !prev)}>{testsCollapsed ? 'Expand' : 'Collapse'}</button>
              </div>

              {!testsCollapsed && (
                <div className="testcase-pane-content">
                  <div className="testcase-tabs">
                    {problem.question.tests.map((_, idx) => {
                      const row = resultByCase[idx];
                      return (
                        <button
                          key={`tab-${idx}`}
                          type="button"
                          className={`testcase-tab ${activeTestCase === idx ? 'active' : ''} ${row ? (row.passed ? 'passed' : 'failed') : ''}`}
                          onClick={() => setActiveTestCase(idx)}
                        >
                          Case {idx + 1}
                        </button>
                      );
                    })}
                  </div>

                  <div className="testcase-detail">
                    <p><strong>Input:</strong> {JSON.stringify(activeCase?.input)}</p>
                    <p><strong>Expected:</strong> {JSON.stringify(activeCase?.expected)}</p>
                    <p><strong>Result:</strong> {resultByCase[activeTestCase] ? (resultByCase[activeTestCase].passed ? 'Passed' : `Failed (${resultByCase[activeTestCase].actual})`) : 'Run testcases to view output'}</p>
                    {runError ? <p className="run-error">{runError}</p> : null}
                  </div>
                </div>
              )}
            </div>
            </> : <div className="problem-example-card" style={{ margin: '1.5rem' }}><h4>Interactive Runner Hidden</h4><p>This question was imported from the large online catalog, but its official statement, examples, and runnable test cases have not been mapped safely into the in-app runner yet.</p><p>To prevent wrong overlaps like Two Sum, Climbing Stairs, or unrelated visualizers appearing on other titles, the editor and test runner are disabled for this entry until it is curated.</p>{problem.sourceUrl ? <p><a href={problem.sourceUrl} target="_blank" rel="noreferrer">Open original problem statement</a></p> : null}</div>}
          </article>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProblemDetailPage;
